<template>
  <div class="task-card" :class="['urgency-' + task.urgency, { done: task.status === 'done' }]">
    <div class="urgency-stripe" :style="{ background: urgencyColor }"></div>

    <div class="card-body">
      <div class="card-top">
        <span v-if="task.project_name" class="project-tag" :style="{ background: task.project_color + '22', color: task.project_color, borderColor: task.project_color + '55' }">
          {{ task.project_name }}
        </span>
        <span class="spacer"></span>
        <span :class="['badge', 'badge-' + task.urgency]">{{ t('urgency.' + task.urgency) }}</span>
      </div>

      <h3 class="card-title">{{ task.title }}</h3>
      <p v-if="task.description" class="card-desc">{{ task.description }}</p>

      <div class="card-meta">
        <span v-if="task.due_at" class="meta-item" :class="{ overdue: isOverdue }">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          {{ formatDate(task.due_at) }}
        </span>
        <span v-if="task.assignee_name" class="meta-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          {{ task.assignee_name }}
        </span>
        <span v-if="task.visibility === 'private'" class="meta-item private-tag" :title="t('taskCard.private')">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          {{ t('taskCard.private') }}
        </span>
      </div>

      <div class="card-footer">
        <span :class="['badge', 'badge-' + task.status]">{{ statusLabel }}</span>
        <div class="card-actions">
          <!-- Timer: internal users only -->
          <TaskTimer
            v-if="!isExternal"
            :task-id="task.id"
            :timer-seconds="task.timer_seconds"
            :timer-started-at="task.timer_started_at"
            @update="onTimerUpdate"
          />
          <!-- Edit: internal users only -->
          <button v-if="!isExternal" class="btn btn-sm btn-icon" :title="t('common.edit')" @click.stop="$emit('edit', task)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <!-- Complete: everyone -->
          <button
            v-if="task.status !== 'done'"
            class="btn btn-sm btn-icon"
            :title="t('taskCard.markDone')"
            @click.stop="$emit('complete', task)"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
          </button>
          <!-- Reopen: manager only (employees and external clients cannot undo) -->
          <button
            v-else-if="isManager"
            class="btn btn-sm btn-icon"
            :title="t('taskCard.reopen')"
            @click.stop="$emit('reopen', task)"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
          </button>
          <!-- Archive: internal users only -->
          <button v-if="!isExternal" class="btn btn-sm btn-icon danger-icon" :title="t('taskCard.archive')" @click.stop="$emit('archive', task)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import TaskTimer from '@/components/TaskTimer.vue'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'

const URGENCY_COLORS = { low: '#64748b', normal: '#6366f1', high: '#f59e0b', critical: '#ef4444' }

export default {
  name: 'TaskCard',
  components: { TaskTimer },
  props: { task: { type: Object, required: true } },
  emits: ['edit', 'complete', 'reopen', 'archive', 'timer-update'],
  setup(props, { emit }) {
    const { isManager: _isManager, isExternalClient } = useAuth()
    const { t, locale } = useI18n()

    const isExternal   = computed(() => isExternalClient())
    const isManager    = computed(() => _isManager())
    const urgencyColor = computed(() => URGENCY_COLORS[props.task.urgency] || '#6366f1')
    const statusLabel  = computed(() => t('status.' + props.task.status))
    const isOverdue    = computed(() => {
      if (!props.task.due_at || props.task.status === 'done') return false
      return new Date(props.task.due_at) < new Date()
    })

    function formatDate(dt) {
      if (!dt) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(dt).toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' })
    }

    function onTimerUpdate(payload) {
      emit('timer-update', { taskId: props.task.id, ...payload })
    }

    return { isExternal, isManager, urgencyColor, statusLabel, isOverdue, formatDate, onTimerUpdate, t }
  },
}
</script>

<style scoped>
.task-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  transition: border-color .15s, box-shadow .15s;
  position: relative;
}
.task-card:hover { border-color: var(--primary); box-shadow: 0 2px 12px rgba(99,102,241,.2); }
.task-card.done  { opacity: .65; }

.urgency-stripe { width: 4px; flex-shrink: 0; }
.urgency-low      .urgency-stripe { background: var(--low); }
.urgency-normal   .urgency-stripe { background: var(--normal); }
.urgency-high     .urgency-stripe { background: var(--high); }
.urgency-critical .urgency-stripe { background: var(--critical); }

.card-body { flex: 1; padding: 14px 16px; min-width: 0; }
.card-top  { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.spacer    { flex: 1; }

.project-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid;
}
.card-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.task-card.done .card-title { text-decoration: line-through; }

.card-desc {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--muted);
}
.meta-item.overdue { color: var(--danger); }
.private-tag { color: var(--warning); }

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.card-actions { display: flex; align-items: center; gap: 4px; }
.btn-icon {
  color: var(--muted);
  width: 28px; height: 28px;
  justify-content: center;
}
.btn-icon:hover { color: var(--text); background: var(--hover); }
.danger-icon:hover { color: var(--danger); background: rgba(239,68,68,.1); }
</style>
